import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);

    // Fix bad inline styles + ensure ARIA is correct on initial load
    this.ensureCartBubbleA11y();

    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleA11y();
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Ensures the cart bubble live region is correctly configured and
   * removes any problematic inline styles that trigger contrast scanners.
   */
  ensureCartBubbleA11y = () => {
    const { cartBubbleText } = this.refs;
    if (!cartBubbleText) return;

    // Ensure live region is properly defined
    cartBubbleText.setAttribute('role', 'status');
    cartBubbleText.setAttribute('aria-live', 'polite');
    cartBubbleText.setAttribute('aria-atomic', 'true');

    // Find the SR-only node inside and remove inline styles that cause false contrast alerts
    const srOnly = cartBubbleText.querySelector('.visually-hidden');
    if (srOnly) {
      srOnly.removeAttribute('style'); // <-- this is the key fix for your screenshot
    }
  };

  /**
   * Updates the SR-only announcement text inside the live region.
   * @param {number} count
   */
  updateCartAnnouncement = (count) => {
    const { cartBubbleText } = this.refs;
    if (!cartBubbleText) return;

    let srOnly = cartBubbleText.querySelector('.visually-hidden');

    // If theme markup ever changes and the span is missing, create it safely.
    if (!srOnly) {
      srOnly = document.createElement('span');
      srOnly.className = 'visually-hidden';
      cartBubbleText.prepend(srOnly);
    }

    // Keep it clean (no inline styles that trigger scanner contrast alerts)
    srOnly.removeAttribute('style');

    // Update announcement
    srOnly.textContent = `Total items in cart: ${count}`;
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // Show/hide UI
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    // Compute new count
    const nextCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;
    this.currentCartCount = nextCount;

    // Update SR announcement + ensure ARIA is correct each time
    this.ensureCartBubbleA11y();
    this.updateCartAnnouncement(nextCount);

    this.classList.toggle('header-actions__cart-icon--has-cart', nextCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(nextCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);
    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (value === visibleCount) {
        // Still ensure a11y markup is clean
        this.ensureCartBubbleA11y();
        this.updateCartAnnouncement(parseInt(value, 10) || 0);
        return;
      }

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);
        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}